import { cn, Skeleton } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { placeholderCount, placeholderKeys } from '../lib/placeholder-count'

/**
 * Built-in strings of {@link FormSkeleton}, overridable per locale (tech §59).
 *
 * @property loading - Accessible name of the status container. Default `Loading`.
 */
export interface FormSkeletonLabels {
	loading: string
}

const DEFAULT_LABELS: FormSkeletonLabels = { loading: 'Loading' }

/** Field placeholders when the consumer supplies no `fields`. */
const DEFAULT_FIELDS = 3

/**
 * Props for the {@link FormSkeleton} molecule.
 *
 * @property fields - Field placeholders. Defaults to 3; rounded down, never below 1.
 * @property className - Extra Tailwind classes merged onto the status container.
 * @property labels - Localised built-in strings; each omitted key falls back to English.
 *
 * @example
 * ```tsx
 * {isLoading ? <FormSkeleton fields={5} /> : <SettingsForm {...settings} />}
 * ```
 */
export interface FormSkeletonProps {
	fields?: number
	className?: string
	labels?: Partial<FormSkeletonLabels>
}

/**
 * Loading placeholder shaped like a form: label-over-control fields, then the submit action.
 *
 * @remarks
 * - **Accessibility:** one `role="status"` container named `labels.loading` announces the wait;
 *   the placeholders are `aria-hidden`, so a screen reader hears the status once.
 * - **Composition:** each placeholder is the primitives-layer `<Skeleton>`.
 * - **Styling:** every spacing and size class is a `ds-*` token utility.
 *
 * @see packages/ui-components/src/form-skeleton/form-skeleton.stories.tsx for visual examples
 */
export function FormSkeleton({ fields, className, labels }: FormSkeletonProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...labels }
	return (
		<div role="status" aria-label={text.loading} className={cn('flex flex-col gap-ds-4', className)}>
			<div aria-hidden="true" className="flex flex-col gap-ds-4">
				{placeholderKeys(placeholderCount(fields, DEFAULT_FIELDS)).map((key) => (
					<div key={key} data-testid="form-skeleton-field" className="flex flex-col gap-ds-1.5">
						<Skeleton data-testid="form-skeleton-label" shape="text" className="w-1/4" />
						<Skeleton data-testid="form-skeleton-control" className="h-ds-10 w-full" />
					</div>
				))}
				<Skeleton data-testid="form-skeleton-action" className="h-ds-10 w-ds-24 self-end" />
			</div>
		</div>
	)
}
